import { useState } from "react";
import { Importer, ImporterField, deDE } from "react-csv-importer";
import { geocodeByAddress, getLatLng } from "react-google-places-autocomplete";
import { format } from "date-fns";
import { toast } from "react-toastify";

import "react-csv-importer/dist/index.css";

import studentsApi from "../../api/students.api";
import { mapStudentRowToModel } from "./mapRowToModel";

import Loader from "@components/Loader";

import stores from "@stores";

const waitFor = (ms) =>
  new Promise((resolve, _reject) => {
    setTimeout(resolve, ms);
  });

const useStudentsImport = ({ projectId, onImportCompleted = () => ({}) }) => {
  const { studentsStore } = stores;

  const [showImportBar, setShowImportBar] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const processRow = async (row) => {
    try {
      const mappedStudent = mapStudentRowToModel(row);

      if (mappedStudent.address !== "") {
        const [geocode] = await geocodeByAddress(mappedStudent.address);
        const { lat, lng } = await getLatLng(geocode);

        mappedStudent.coordinates = [lng, lat];
        mappedStudent.placeId = geocode.place_id;
        // intentionally make a pause, to not exceed google api usage limits.
        await waitFor(1000);
        setLoading(true);
        await studentsApi.add({
          ...mappedStudent,
          birthday: mappedStudent?.birthday
            ? format(new Date(mappedStudent?.birthday), "dd. MMMM yyyy")
            : "",
          project: projectId,
        });
        await studentsStore.fetchStudentsList(projectId);
      } else {
        toast.error(
          `Unable to import following student, address is empty: ${row?.lastName} ${row?.firstName}`
        );
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setErrors([...errors, error]);
      toast.error(`Error happened with parsing the row ${error}`);
    }
  };

  const ImportView = showImportBar ? (
    <>
      {loading && <Loader filled />}
      <Importer
        dataHandler={async (rows, { _startIndex }) => {
          for (const row of rows) {
            await processRow(row);
          }
        }}
        locale={deDE}
        defaultNoHeader={false}
        restartable={false}
        onComplete={({ file, preview, fields, columnFields }) => {
          onImportCompleted();
        }}
        onClose={({ file, preview, fields, columnFields }) => {
          setShowImportBar(false);
        }}
        skipEmptyLines
      >
        <ImporterField name="lastName" label="Name" />
        <ImporterField name="firstName" label="Vorname" />
        <ImporterField name="address" label="Adresse" optional />
        <ImporterField name="postalCode" label="PLZ" optional />
        <ImporterField name="city" label="Stadt" optional />
        <ImporterField name="gender" label="Geschlecht" />
        <ImporterField name="teacher" label="Letzte Lehrperson" optional />
        <ImporterField name="teacherName" label="LP" optional />
        <ImporterField name="bestFriendName" label="Freunde" optional />
        <ImporterField name="knowledgeLevel" label="Anforderungen" />
        <ImporterField
          name="knowledgeLevelNote"
          label="Anforderungen wurde abgeklärt (SPL, Arzt)"
          optional
        />
        <ImporterField
          name="knowledgeLevelSpecialNote"
          label="Anforderungen Grund"
          optional
        />
        <ImporterField name="inS" label="InS" optional />
        <ImporterField name="specialNeeds" label="Fördermassnahmen" optional />
        <ImporterField
          name="specialNeedsNote"
          label="Fördermassnahmen Weitere"
          optional
        />
        <ImporterField
          name="germanLanguageLevel"
          label="Deutschkenntnisse der Eltern"
          optional
        />
        <ImporterField
          name="helpWithStudy"
          label="Lernbegleitung durch SHP"
          optional
        />
        <ImporterField name="note" label="Bemerkungen" optional />
        <ImporterField
          name="specialNote"
          label="Stärken / Ressource"
          optional
        />
      </Importer>
    </>
  ) : (
    ""
  );

  return {
    openImport: () => setShowImportBar(true),
    closeImport: () => setShowImportBar(false),
    isImportActive: showImportBar,
    ImportView,
  };
};

export default useStudentsImport;
